import React, { useEffect, useState, useRef } from "react";
import styled, { keyframes } from "styled-components";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Button from "src/components/Button/Button";
import TitleSection from "src/components/TitleSection/TitleSection";
import VideoCarousel from "src/components/VideoCarousel/VideoCarousel";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";
import { clampValue, isNumericValue } from "src/utils/numberUtils";


const TournamentPlayerSelectorSection = ({
  title,
  text,
  backgroundImage,
  overlayColor = "",
  players = [],
  arrowIcon,
  lockInLabel = "LOCK IN PLAYER",
  selectedContender,
  onContenderChange,
  disabled = true,
}) => {
  const [nav1, setNav1] = useState(null);
  const [nav2, setNav2] = useState(null);
  const videoSliderRef = useRef(null);
  const detailsSliderRef = useRef(null);

  const resolvedBackgroundImage = useResponsiveImageUrl(backgroundImage);
  const hasPlayers = Array.isArray(players) && players.length > 0;

  // Set up slider refs after mount
  useEffect(() => {
    setNav1(videoSliderRef.current);
    setNav2(detailsSliderRef.current);
  }, []);

  // Sync with parent component on slide change
  const handleSlideChange = (index, player) => {
    if (onContenderChange && player) {
      const contender = (player?.name || player?.nickname || "").trim();
      if (contender && contender !== selectedContender) {
        onContenderChange(contender);
      }
    }
  };

  const handleLockInClick = (player) => {
    // Update selected contender
    if (onContenderChange && player) {
      const contender = (player?.name || player?.nickname || "").trim();
      if (contender) {
        onContenderChange(contender);
      }
    }

    // Scroll to the form section with offset for header
    const formSection = document.querySelector('.creator-form-wrap');
    if (formSection) {
      const headerOffset = 100; // Adjust this value based on your header height
      const elementPosition = formSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <Section
      $backgroundImage={resolvedBackgroundImage}
      $overlayColor={overlayColor}
    >
      {(title || text) && (
        <Header>
          <TitleSection
            className="player-selector-header-title"
            dark={false}
            as="h2"
            title={title || ""}
            text={text || ""}
            borderColor={"none"}
          />
        </Header>
      )}

      <Container>
        {hasPlayers && (
          <SelectorLayout>
            <LeftColumn>
              <PlayerStage>
                <PlayerMediaWrap>
                  <VideoCarousel
                    items={players}
                    arrowIcon={arrowIcon}
                    onSlideChange={handleSlideChange}
                    sliderRef={videoSliderRef}
                    asNavFor={nav2}
                    videoSoundControl={false}
                    renderTitle={(player) => (
                      <PlayerTitleBlock>
                        <PlayerName>{player.name}</PlayerName>
                        {player.nickname && <PlayerNickname>{player.nickname}</PlayerNickname>}
                      </PlayerTitleBlock>
                    )}
                  />
                </PlayerMediaWrap>
              </PlayerStage>
            </LeftColumn>

            <CardColumn>
              <DetailsSlider
                asNavFor={nav1}
                ref={detailsSliderRef}
                arrows={false}
                fade={false}
                speed={300}
                cssEase="cubic-bezier(0.4, 0, 0.2, 1)"
                swipe={false}
                draggable={false}
                infinite={true}
                slidesToShow={1}
                slidesToScroll={1}
              >
                {players.map((player, index) => (
                  <div key={index}>
                    <AttributesCard>
                      <h3>{player.attributesTitle || "ATTRIBUTES"}</h3>
                      {Array.isArray(player.attributes) &&
                        player.attributes.map((attribute, attrIndex) => {
                          const hasNumericValue = isNumericValue(attribute?.value);
                          const value = hasNumericValue ? clampValue(attribute?.value) : 0;
                          const displayValue = attribute?.displayValue || (hasNumericValue ? value : attribute?.value || "");
                          return (
                            <AttributeRow
                              key={`${attribute?.label || "attribute"}-${attrIndex}`}
                              $isTextOnly={!hasNumericValue}
                            >
                              <AttributeTop>
                                <span>{attribute?.label}</span>
                                <strong>{displayValue}</strong>
                              </AttributeTop>
                              {hasNumericValue ? (
                                <ProgressTrack>
                                  <ProgressValue
                                    $width={value}
                                    $progressColor={attribute?.progressColor}
                                  />
                                </ProgressTrack>
                              ) : null}
                            </AttributeRow>
                          );
                        })}
                    </AttributesCard>

                    <LockInButton
                      label={player.lockInLabel || lockInLabel}
                      bgColor="#cf2818"
                      hoverBgColor="#db4026"
                      color="var(--white)"
                      hoverColor="var(--white)"
                      size="large"
                      width="wide"
                      className="no-spacing mt-4"
                      disabled={disabled}
                      onClick={() => handleLockInClick(player)}
                    />
                  </div>
                ))}
              </DetailsSlider>
            </CardColumn>
          </SelectorLayout>
        )}
      </Container>
    </Section>
  );
};

const Section = styled.section`
  position: relative;
  background-image: ${({ $backgroundImage }) =>
    $backgroundImage ? `url(${$backgroundImage})` : "none"};
  background-size: cover;
  background-position: center center;
  border-top: 1px solid rgba(180, 186, 196, 0.55);
  border-bottom: 1px solid rgba(180, 186, 196, 0.55);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.1),
    inset 0 -1px 0 rgba(255, 255, 255, 0.1);
  margin-top: 20px;
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: ${({ $overlayColor }) => $overlayColor};
  }

  @media (max-width: 767px) {
    padding: 0 0 28px;
  }
`;

const Container = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1440px;
  margin: 0 auto;
`;

const DetailsSlider = styled(Slider)`
  width: 100%;

  .slick-list {
    overflow: hidden;
    margin: 0 -15px;
  }

  .slick-slide {
    padding: 0 15px;

    > div {
      outline: none;
    }
  }

  @media (max-width: 767px) {
    .slick-list {
      margin: 0;
    }

    .slick-slide {
      padding: 0 10px;
    }
  }
`;

const Header = styled.div`
  text-align: center;
  padding: 40px 20px;
  background: linear-gradient(
    to bottom,
    rgba(3, 7, 15, 0.98) 0%,
    rgba(5, 11, 22, 0.92) 65%,
    rgba(5, 11, 22, 0.88) 100%
  );
  border-bottom: 1px solid rgba(101, 115, 138, 0.4);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    inset 0 -1px 0 rgba(255, 255, 255, 0.04);
  width: 100%;

  .player-selector-header-title {
    padding: 0;
    span {
      display: none;
    }
  }

  .player-selector-header-title > h1,
  .player-selector-header-title > h2 {
    margin: 0;
    margin-bottom: 12px;
    color: #f4e0a7;
    text-transform: uppercase;
    line-height: 0.95;
    font-weight: 900;
    letter-spacing: 1px;
    font-size: 45px;
  }


  @media (max-width: 991px) {
    padding: 40px 20px 20px 20px;

    .player-selector-header-title > h1,
    .player-selector-header-title > h2 {
      letter-spacing: 0.4px;
      font-size: 32px;
    }
  }
`;

const SelectorLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
  grid-template-rows: auto minmax(0, 1fr);
  grid-template-areas:
    "playerTitle ."
    "left card";
  column-gap: 80px;
  align-items: stretch;
  margin: 40px auto;

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      "playerTitle"
      "left"
      "card";
    gap: 26px;
    margin: 40px auto 20px auto;
  }
`;

const LeftColumn = styled.div`
  grid-area: left;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: stretch;
  min-width: 0;
  width: 100%;
  height: 100%;
  align-self: stretch;
  position: relative;
`;

const PlayerStage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;

  @media (max-width: 991px) {
    max-width: 620px;
    margin: 0 auto;
  }

  @media (max-width: 767px) {
    max-width: 100%;
    width: 100%;
    align-items: center;
    justify-content: center;
    padding: 0 20px;
  }
`;

const PlayerMediaWrap = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  min-height: 640px;
  padding: 0;

  @media (max-width: 991px) {
    min-height: 500px;
  }

  @media (max-width: 767px) {
    min-height: 440px;
  }
`;

const ArrowButton = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  line-height: 0;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 20;
  flex: 0 0 auto;

  &.slick-prev-custom {
    left: 20px;
  }

  &.slick-next-custom {
    right: 20px;
  }

  span {
    font-size: 30px;
    line-height: 1;
    color: rgba(250, 225, 169, 0.9);
  }

  img {
    width: 74px;
    height: auto;
    display: block;
  }

  @media (max-width: 991px) {
    img {
      width: 58px;
    }

    &.slick-prev-custom {
      left: 10px;
    }

    &.slick-next-custom {
      right: 10px;
    }
  }

  @media (max-width: 767px) {
    width: 60px;
    height: 60px;

    &.slick-prev-custom {
      left: 20px;
    }

    &.slick-next-custom {
      right: 20px;
    }

    img {
      width: 60px;
    }
  }
`;

const PlayerContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${({ $isVideo }) => ($isVideo ? "center" : "flex-end")};
  max-height: ${({ $isVideo }) => ($isVideo ? "640px" : "640px")};
  height: fit-content;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  padding-bottom: ${({ $isVideo }) => ($isVideo ? "0" : "34px")};
  z-index: 1;
  flex: 0 0 auto;
  min-height: 740px;

  /* &::after {
    content: "";
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: 4px;
    width: 82%;
    height: 72px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 143, 45, 0.72) 0%, rgba(255, 143, 45, 0.14) 58%, transparent 100%);
    filter: blur(10px);
    z-index: 0;
  } */

  @media (max-width: 991px) {
    min-height: 440px;
    height: 440px;
    width: 100%;
    max-width: 100%;
    padding-bottom: ${({ $isVideo }) => ($isVideo ? "0" : "24px")};
  }
`;

const PlayerTitleBlock = styled.div`
  grid-area: playerTitle;
  width: 100%;
  text-align: center;
  margin-bottom: 8px;
  pointer-events: none;
  position: relative;
  z-index: 10;
`;

const PlayerName = styled.h3`
  margin: 0;
  color: #ffe5a5;
  text-transform: uppercase;
  font-size: 2.5rem;
  font-weight: 900;
  letter-spacing: 1px;
  text-shadow: 0 3px 12px rgba(0, 0, 0, 0.65);
  line-height: 0.96;

  @media (max-width: 767px) {
    font-size: 2rem;
  }
`;

const PlayerNickname = styled.p`
  margin: 6px 0 0;
  color: #f7e7af;
  font-size: 1.05rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  line-height: 1.15;

  &::before,
  &::after {
    content: "";
    display: block;
    width: 88px;
    height: 1px;
    background: rgba(201, 202, 198, 0.65);
  }

  @media (max-width: 767px) {
    font-size: 0.95rem;
    gap: 12px;

    &::before,
    &::after {
      width: 42px;
    }
  }
`;


const CardColumn = styled.div`
  grid-area: card;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  justify-self: center;
  height: 100%;
  width: 100%;
  max-width: 740px;
  align-self: center;
  min-width: 0;

  @media (max-width: 991px) {
    width: 100%;
    max-width: 100%;
    padding: 0 20px;
  }
`;

const AttributesCard = styled.div`
  position: relative;
  background: linear-gradient(180deg, rgba(9, 9, 9, 0.7) 0%, rgba(49, 58, 75, 0.72) 46%, rgba(19, 28, 43, 0.82) 100%);
  border: 2px solid rgba(155, 170, 194, 0.68);
  border-radius: 24px;
  width: 100%;
  max-width: 100%;
  padding: 22px 20px 18px;
  overflow: hidden;
  backdrop-filter: blur(6px) saturate(120%);
  -webkit-backdrop-filter: blur(6px) saturate(120%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.32),
    inset 0 -1px 0 rgba(21, 34, 57, 0.9),
    0 14px 28px rgba(0, 0, 0, 0.38);
  box-sizing: border-box;

  &::before {
    content: "";
    position: absolute;
    top: -8%;
    right: 7%;
    width: 18%;
    height: 120%;
    transform: skewX(-20deg);
    background: linear-gradient(180deg, rgba(15, 14, 14, 0.2) 0%, rgba(255, 255, 255, 0.03) 100%);
    pointer-events: none;
  }

  &::after {
    content: "";
    position: absolute;
    top: -8%;
    right: 26%;
    width: 10%;
    height: 120%;
    transform: skewX(-20deg);
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.11) 0%, rgba(255, 255, 255, 0) 100%);
    pointer-events: none;
  }

  h3 {
    color: var(--white);
    text-align: center;
    margin-bottom: 18px;
    font-size: clamp(1.95rem, 2.8vw, 2.45rem);
    font-weight: 900;
    letter-spacing: 0.5px;
    text-shadow: 0 3px 12px rgba(0, 0, 0, 0.55);
    position: relative;
    z-index: 1;
    text-transform: uppercase;
    border-bottom: 1px solid rgba(155, 170, 194, 0.68);
    padding-bottom: 10px;
  }

  @media (max-width: 991px) {
    border-radius: 20px;
    padding: 16px 14px 12px;

    h3 {
      margin-bottom: 14px;
      font-size: 1.9rem;
    }
  }
`;

const AttributeRow = styled.div`
  margin-bottom: 10px;
  position: relative;
  z-index: 1;
  ${({ $isTextOnly }) =>
    $isTextOnly
      ? `
    border-bottom: 1px solid rgba(185, 195, 210, 0.18);
  `
      : ""}
`;

const AttributeTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;

  span {
    color: var(--white);
    font-weight: 900;
    font-size: 1.4rem;
    text-transform: uppercase;
    letter-spacing: 0.35px;
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);
  }

  strong {
    color: #f4ab2f;
    font-size: 1.4rem;
    font-weight: 900;
    line-height: 1;
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);
    text-align: right;
  }

  @media (max-width: 991px) {
    margin-bottom: 5px;

    span {
      font-size: 1.1rem;
      line-height: 1.1;
    }

    strong {
      font-size: 1.1rem;
    }
  }
`;

const ProgressTrack = styled.div`
  height: 21px;
  border-radius: 999px;
  background: rgba(18, 30, 49, 0.78);
  border: 1px solid rgba(112, 128, 153, 0.6);
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.45);

  @media (max-width: 767px) {
    height: 18px;
  }
`;

const ProgressValue = styled.div`
  height: 100%;
  width: ${({ $width }) => $width}%;
  border-radius: 999px;
  background: ${({ $progressColor }) =>
    $progressColor ||
    "linear-gradient(90deg, #f6df5e 0%, #f59322 52%, #de2819 100%)"};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.25);
`;

const LockInButton = styled(Button)`
  width: 100%;
  max-width: 350px;
  margin: 18px auto 0;
  border: 2px solid rgba(213, 112, 86, 0.9);
  border-radius: 12px;
  background: linear-gradient(180deg, #dc4028 0%, #a31812 100%);
  padding: 1rem;
  text-transform: uppercase;

  &:hover,
  &.active {
    background: linear-gradient(180deg, #e14a2e 0%, #af1e15 100%);
    color: var(--white);
  }

  > div > span {
    font-weight: 900;
    font-size: 1.5rem;
  }

  @media (max-width: 767px) {
    width: 100%;
    margin-top: 18px;

    > div > span {
      font-size: 1.15rem;
    }
  }
`;

export default TournamentPlayerSelectorSection;
